// Portfolio Administration v1.0 PR 5 — Local Enforcers registry list-page
// filter row. Multi-select chips for enforcer type + status; free-text
// search + jurisdictions multi-select. Mirrors VendorsListFilters in
// shape per the parameterised registry pattern.

import { Input } from "@/design-system/components/ui";
import { FilterChipGroup } from "@/components/portfolioAdmin/registry/FilterChipGroup";

// Spec §9.2 — 12 enforcer types.
export const ENFORCER_TYPE_OPTIONS = [
  { value: "law-firm", label: "Law firm" },
  { value: "collection-agency", label: "Collection agency" },
  { value: "receiver-trustee", label: "Receiver / trustee" },
  { value: "process-server", label: "Process server" },
  { value: "auctioneer", label: "Auctioneer" },
  { value: "liquidator", label: "Liquidator" },
  { value: "appraiser-for-recovery", label: "Appraiser (recovery)" },
  { value: "locksmith", label: "Locksmith" },
  { value: "security-firm", label: "Security firm" },
  { value: "repossession-firm", label: "Repossession firm" },
  { value: "skip-tracer", label: "Skip-tracer" },
  { value: "investigator", label: "Investigator" },
] as const;

export const ENFORCER_STATUS_OPTIONS = [
  { value: "prospect", label: "Prospect" },
  { value: "onboarding", label: "Onboarding" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "terminated", label: "Terminated" },
] as const;

// A representative subset of US states; the registry can be extended
// without code changes via the multi-select free-form input above.
export const COMMON_STATE_OPTIONS = [
  { value: "TX", label: "TX" },
  { value: "OK", label: "OK" },
  { value: "LA", label: "LA" },
  { value: "NM", label: "NM" },
  { value: "AR", label: "AR" },
  { value: "CA", label: "CA" },
  { value: "NV", label: "NV" },
  { value: "AZ", label: "AZ" },
  { value: "UT", label: "UT" },
  { value: "FL", label: "FL" },
  { value: "GA", label: "GA" },
  { value: "NY", label: "NY" },
] as const;

export interface LocalEnforcersListFiltersProps {
  types: string[];
  statuses: string[];
  jurisdictions: string[];
  search: string;
  onTypesChange: (next: string[]) => void;
  onStatusesChange: (next: string[]) => void;
  onJurisdictionsChange: (next: string[]) => void;
  onSearchChange: (next: string) => void;
  onClear: () => void;
}

export function LocalEnforcersListFilters({
  types,
  statuses,
  jurisdictions,
  search,
  onTypesChange,
  onStatusesChange,
  onJurisdictionsChange,
  onSearchChange,
  onClear,
}: LocalEnforcersListFiltersProps) {
  const hasActive =
    types.length > 0 ||
    statuses.length > 0 ||
    jurisdictions.length > 0 ||
    search.length > 0;
  return (
    <>
      <div className="flex flex-col gap-space-1 min-w-[220px]">
        <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
          Search
        </span>
        <Input
          type="search"
          value={search}
          placeholder="Search by name…"
          onChange={(e) => onSearchChange(e.target.value)}
          data-testid="enforcers-filter-search"
        />
      </div>
      <FilterChipGroup
        label="Type"
        testId="enforcers-filter-type"
        options={ENFORCER_TYPE_OPTIONS}
        selected={types}
        onChange={onTypesChange}
      />
      <FilterChipGroup
        label="Status"
        testId="enforcers-filter-status"
        options={ENFORCER_STATUS_OPTIONS}
        selected={statuses}
        onChange={onStatusesChange}
      />
      <FilterChipGroup
        label="Jurisdictions"
        testId="enforcers-filter-jurisdictions"
        options={COMMON_STATE_OPTIONS}
        selected={jurisdictions}
        onChange={onJurisdictionsChange}
      />
      {hasActive ? (
        <button
          type="button"
          className="text-small text-gold-shadow hover:underline self-end pb-[6px]"
          onClick={onClear}
          data-testid="enforcers-filter-clear"
        >
          Clear filters
        </button>
      ) : null}
    </>
  );
}
